import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Edit2 } from "lucide-react";
import GlobalLoader from "../ui/GlobalLoader";
import { EditCustomerPackageModal } from "../sunbeds/EditCustomerPackageModal";
import { useToast } from "../../hooks/useToast";
import {
  ApiError,
  assignSunbedMembership,
  cancelSunbedMembership,
  getSunbedCustomerPackages,
  getSunbedMembershipPlans,
  getSunbedMemberships,
  getSunbedSessionLogs,
  parseSunbedMinutes,
  SunbedCustomerPackageApi,
  SunbedMembershipApi,
  SunbedMembershipPlanApi,
  SunbedSessionLogApiItem,
  updateSunbedCustomerPackage,
} from "../../services/sunbeds";
import { cn } from "../../lib/utils";

const SESSIONS_PAGE_SIZE = 5;

interface CustomerPackageRow {
  id: string;
  packageName: string;
  usedMinutes: number;
  remainingMinutes: number;
  sunbedId?: number;
}

interface CustomerSunbedSectionProps {
  customerId: number;
  customerName: string;
}

function namesMatch(a?: string | null, b?: string | null): boolean {
  return String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function sessionStatusClass(badgeClass?: string): string {
  if (badgeClass === "badge-success") return "bg-green-100 text-green-700";
  if (badgeClass === "badge-primary") return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-gray-700";
}

export default function CustomerSunbedSection({
  customerId,
  customerName,
}: CustomerSunbedSectionProps) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<CustomerPackageRow[]>([]);
  const [memberships, setMemberships] = useState<SunbedMembershipApi[]>([]);
  const [plans, setPlans] = useState<SunbedMembershipPlanApi[]>([]);
  const [assignPlanId, setAssignPlanId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [sessionLogs, setSessionLogs] = useState<SunbedSessionLogApiItem[]>([]);
  const [editingPackage, setEditingPackage] = useState<CustomerPackageRow | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string>("all");
  const [visibleSessionCount, setVisibleSessionCount] = useState(SESSIONS_PAGE_SIZE);
  const [sessionsExpanded, setSessionsExpanded] = useState(true);

  const fetchSunbedData = useCallback(async () => {
    setLoading(true);
    try {
      const [packagesRes, logsRes, membershipsRes, plansRes] = await Promise.all([
        getSunbedCustomerPackages(),
        getSunbedSessionLogs(),
        getSunbedMemberships(customerId),
        getSunbedMembershipPlans(),
      ]);

      const customerPackages: CustomerPackageRow[] = (packagesRes.data || [])
        .filter((pkg: SunbedCustomerPackageApi) => pkg.user_id === customerId)
        .map((pkg) => ({
          id: String(pkg.id),
          packageName: pkg.package_name || pkg.sunbed?.label || "Sunbed Package",
          usedMinutes: parseSunbedMinutes(pkg.used_minutes),
          remainingMinutes: parseSunbedMinutes(pkg.remaining_balance),
          sunbedId: pkg.sunbed_id,
        }));

      setPackages(customerPackages);
      setMemberships(membershipsRes.data || []);
      setPlans((plansRes.data || []).filter((plan) => plan.status === "active"));
      setSessionLogs(
        (logsRes.data || []).filter((log) => namesMatch(log.customer, customerName)),
      );
    } catch (error) {
      console.error("Failed to load customer sunbed data:", error);
      toast.error("Failed to load sunbed details.");
    } finally {
      setLoading(false);
    }
  }, [customerId, customerName]);

  useEffect(() => {
    fetchSunbedData();
  }, [fetchSunbedData]);

  const sessionCountByPackage = useMemo(() => {
    const counts = new Map<string, number>();
    packages.forEach((pkg) => {
      const pkgName = pkg.packageName.toLowerCase();
      const count = sessionLogs.filter((log) =>
        log.sunbed_details?.toLowerCase().startsWith(pkgName),
      ).length;
      counts.set(pkg.id, count);
    });
    return counts;
  }, [packages, sessionLogs]);

  const filteredLogs = useMemo(() => {
    if (selectedPackageId === "all") {
      return sessionLogs;
    }

    const pkg = packages.find((item) => item.id === selectedPackageId);
    if (!pkg) {
      return sessionLogs;
    }

    const pkgName = pkg.packageName.toLowerCase();
    return sessionLogs.filter((log) =>
      log.sunbed_details?.toLowerCase().startsWith(pkgName),
    );
  }, [sessionLogs, selectedPackageId, packages]);

  useEffect(() => {
    setVisibleSessionCount(SESSIONS_PAGE_SIZE);
  }, [selectedPackageId]);

  const visibleLogs = filteredLogs.slice(0, visibleSessionCount);
  const hasMoreSessions = visibleSessionCount < filteredLogs.length;

  const handleUpdatePackage = async (newMinutes: number, sunbedId: number) => {
    if (!editingPackage) {
      throw new Error("No package selected for editing");
    }

    if (newMinutes > editingPackage.remainingMinutes) {
      const errorMessage = `New remaining minutes (${newMinutes}) cannot be greater than the current balance (${editingPackage.remainingMinutes}).`;
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    try {
      const response = await updateSunbedCustomerPackage(
        editingPackage.id,
        newMinutes,
        sunbedId,
      );
      toast.success(response.message || "Package updated successfully.");
      setEditingPackage(null);
      await fetchSunbedData();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      let errorMessage = "Failed to update customer package.";

      if (apiErr.type === "server" || apiErr.type === "network") {
        errorMessage = apiErr.message || errorMessage;
      } else if (apiErr.type === "serverValidation" && apiErr.errors) {
        errorMessage = Object.values(apiErr.errors).join(", ");
      }

      toast.error(errorMessage);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="py-16">
        <GlobalLoader description="Loading sunbed details..." compensateSidebar={false} />
      </div>
    );
  }

  const handleAssignMembership = async () => {
    if (!assignPlanId) {
      toast.error("Select a membership plan first.");
      return;
    }
    setAssigning(true);
    try {
      await assignSunbedMembership({
        user_id: customerId,
        plan_id: Number(assignPlanId),
        // Stripe auto-renew disabled for now
        create_stripe: false,
      });
      toast.success("Membership assigned.");
      setAssignPlanId("");
      await fetchSunbedData();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      const message =
        apiErr.type === "server" || apiErr.type === "network"
          ? apiErr.message
          : apiErr.type === "serverValidation" && apiErr.errors
            ? Object.values(apiErr.errors).join(", ")
            : undefined;
      toast.error(message || "Could not assign membership.");
    } finally {
      setAssigning(false);
    }
  };

  const handleCancelMembership = async (id: number) => {
    try {
      await cancelSunbedMembership(id, true);
      toast.success("Membership will end at the current period.");
      await fetchSunbedData();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      const message =
        apiErr.type === "server" || apiErr.type === "network"
          ? apiErr.message
          : apiErr.type === "serverValidation" && apiErr.errors
            ? Object.values(apiErr.errors).join(", ")
            : undefined;
      toast.error(message || "Could not cancel membership.");
    }
  };

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-[#E5E0EB] bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-[#FAF8FD] px-4 py-3 flex items-center justify-between gap-3">
          <h4 className="text-sm font-bold text-[#402B69]">Memberships</h4>
        </div>
        <div className="px-4 py-3 flex flex-col sm:flex-row gap-2">
          <select
            value={assignPlanId}
            onChange={(e) => setAssignPlanId(e.target.value)}
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="">Assign a plan...</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={assigning}
            onClick={handleAssignMembership}
            className="rounded-lg bg-[#885ABB] px-3 py-2 text-xs font-semibold text-white hover:bg-[#6f47a2] disabled:opacity-50"
          >
            {assigning ? "Assigning..." : "Assign"}
          </button>
        </div>
        {memberships.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-gray-500">No memberships for this customer.</p>
        ) : (
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {memberships.map((membership) => (
              <div key={membership.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="font-semibold text-gray-900">{membership.plan_name}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {membership.status}
                    <span className="mx-1.5">·</span>
                    {membership.unlimited
                      ? "Unlimited"
                      : `${membership.remaining_minutes ?? 0}m left`}
                    {membership.period_end ? (
                      <>
                        <span className="mx-1.5">·</span>
                        until {membership.period_end}
                      </>
                    ) : null}
                  </p>
                </div>
                {membership.status === "active" && (
                  <button
                    type="button"
                    onClick={() => handleCancelMembership(membership.id)}
                    className="text-xs font-semibold text-red-600 hover:underline"
                  >
                    Cancel at period end
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-[#E5E0EB] bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-[#FAF8FD] px-4 py-3">
          <h4 className="text-sm font-bold text-[#402B69]">Packages</h4>
        </div>

        {packages.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500">No prepaid packages for this customer.</p>
        ) : (
        <div className="divide-y divide-gray-100">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 truncate">{pkg.packageName}</p>
                <p className="mt-1 text-xs text-gray-500">
                  Used <strong className="text-gray-700">{pkg.usedMinutes}m</strong>
                  <span className="mx-1.5">·</span>
                  Left{" "}
                  <strong
                    className={cn(pkg.remainingMinutes > 0 ? "text-green-700" : "text-red-600")}
                  >
                    {pkg.remainingMinutes}m
                  </strong>
                  <span className="mx-1.5">·</span>
                  {sessionCountByPackage.get(pkg.id) || 0} sessions
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingPackage(pkg)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#885ABB] px-3 py-2 text-xs font-semibold text-white hover:bg-[#6f47a2]"
              >
                <Edit2 size={14} />
                Edit
              </button>
            </div>
          ))}
        </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setSessionsExpanded((open) => !open)}
          className="flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 text-left"
        >
          <div>
            <h4 className="text-sm font-bold text-[#402B69]">Session history</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {filteredLogs.length} session{filteredLogs.length === 1 ? "" : "s"}
              {selectedPackageId !== "all" ? " for selected package" : " total"}
            </p>
          </div>
          {sessionsExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
          )}
        </button>

        {sessionsExpanded && (
          <div className="p-4 space-y-3">
            {packages.length > 1 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedPackageId("all")}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium border transition-colors",
                    selectedPackageId === "all"
                      ? "bg-[#885ABB] text-white border-[#885ABB]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#885ABB]",
                  )}
                >
                  All ({sessionLogs.length})
                </button>
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => setSelectedPackageId(pkg.id)}
                    className={cn(
                      "rounded-full px-3 py-1 text-xs font-medium border transition-colors max-w-[160px] truncate",
                      selectedPackageId === pkg.id
                        ? "bg-[#885ABB] text-white border-[#885ABB]"
                        : "bg-white text-gray-600 border-gray-200 hover:border-[#885ABB]",
                    )}
                    title={pkg.packageName}
                  >
                    {pkg.packageName} ({sessionCountByPackage.get(pkg.id) || 0})
                  </button>
                ))}
              </div>
            )}

            {visibleLogs.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No sessions for this filter.</p>
            ) : (
              <>
                <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                  {visibleLogs.map((log, index) => (
                    <div
                      key={`session-log-${index}`}
                      className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-xs"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-gray-800 leading-snug">
                          {log.sunbed_details}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold",
                            sessionStatusClass(log.status?.badge_class),
                          )}
                        >
                          {log.status?.label || "—"}
                        </span>
                      </div>
                      <p className="mt-1 text-gray-600">{log.actual_package_mins}</p>
                      <p className="mt-0.5 text-gray-500 line-clamp-2">
                        {stripHtml(log.session_duration || "")}
                      </p>
                    </div>
                  ))}
                </div>

                {hasMoreSessions && (
                  <button
                    type="button"
                    onClick={() => setVisibleSessionCount((count) => count + SESSIONS_PAGE_SIZE)}
                    className="w-full rounded-lg border border-gray-200 py-2 text-xs font-semibold text-[#885ABB] hover:bg-purple-50"
                  >
                    Load more ({filteredLogs.length - visibleSessionCount} remaining)
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <EditCustomerPackageModal
        isOpen={!!editingPackage}
        onClose={() => setEditingPackage(null)}
        onUpdate={handleUpdatePackage}
        currentMinutes={editingPackage?.remainingMinutes || 0}
        currentSunbedId={editingPackage?.sunbedId}
      />
    </section>
  );
}
